import { useState } from 'react';
import { ApiError } from '../api/client';
import {
  useApproveSdRequest,
  useContributeToSdRequest,
  useCreateSdRequest,
  useDeleteSdRequest,
  useSdRequests,
  useWorldOptions,
} from '../api/queries';
import type { SdRequest, User } from '../api/types';
import { Spinner } from '../components/Spinner';
import { useToast } from '../components/Toast';

function formatCoordinates(request: SdRequest): string {
  return `${request.villageX}|${request.villageY}`;
}

/** `x|y` bzw. `x,y`, wie im Spiel üblich geschrieben -- z. B. "574|874". */
const COORDINATES_PATTERN = /^(\d{1,3})\s*[|,]\s*(\d{1,3})$/;

function parseCoordinates(value: string): { x: number; y: number } | null {
  const match = COORDINATES_PATTERN.exec(value.trim());
  if (!match) return null;
  return { x: Number(match[1]), y: Number(match[2]) };
}

/**
 * SD-Anfragen-Seite: anders als "Verwaltung" für ALLE verifizierten
 * Mitglieder offen (serverseitig durchgesetzt über `requireVerifiedPlayer`
 * ohne `requireTribeLeaderOrAdmin`, siehe `sd-request.routes.ts`) -- nur das
 * Genehmigen bleibt Stammesführern/Administratoren vorbehalten, hier rein
 * über `user.role` ein-/ausgeblendet.
 */
export function SdRequestsPage({ user }: { user: User }) {
  const needsWorldChoice = user.world === null;
  const [worldKey, setWorldKey] = useState('');
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;
  const isLeader = user.role === 'tribeLeader' || user.role === 'admin';

  const requests = useSdRequests(activeWorld || undefined);
  const approve = useApproveSdRequest(activeWorld || undefined);
  const remove = useDeleteSdRequest(activeWorld || undefined);
  const toast = useToast();

  const rows = requests.data?.requests ?? [];
  const pending = rows.filter((r) => r.status === 'pending');
  const approved = rows.filter((r) => r.status === 'approved');
  const toApprove = isLeader ? pending : [];
  const ownPending = pending.filter((r) => r.createdBy === user.id);

  const confirmApprove = (request: SdRequest) => {
    approve.mutate(
      { id: request.id },
      {
        onSuccess: () => toast.push('success', `SD-Anfrage für ${formatCoordinates(request)} wurde genehmigt.`),
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  const confirmDelete = (request: SdRequest, label: string) => {
    if (!window.confirm(`SD-Anfrage für ${formatCoordinates(request)} ${label}?`)) return;
    remove.mutate(
      { id: request.id },
      {
        onSuccess: () => toast.push('success', 'SD-Anfrage wurde entfernt.'),
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>SD-Anfragen</h1>
          <p className="muted">
            Sammeldeff-Pakete anfragen und beitragen — eine gemeinsame Liste für alle Mitglieder dieser Welt.
          </p>
        </div>
      </div>

      {needsWorldChoice && (
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setWorldKey(event.target.value)} required>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {needsWorldChoice && !worldKey && <p className="muted center pad">Wähle eine Welt, um ihre SD-Anfragen zu sehen.</p>}

      {activeWorld && (
        <>
          <NewSdRequestForm worldKey={activeWorld} />

          {requests.isPending && <Spinner label="SD-Anfragen werden geladen …" />}

          {requests.isError && (
            <div className="flash flash-error">
              {requests.error instanceof ApiError ? requests.error.message : 'SD-Anfragen konnten nicht geladen werden.'}
            </div>
          )}

          {isLeader && toApprove.length > 0 && (
            <>
              <h2>Zu genehmigen</h2>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Dorf</th>
                      <th>Pakete</th>
                      <th>Beschreibung</th>
                      <th>Von</th>
                      <th className="right">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {toApprove.map((request) => (
                      <tr key={request.id}>
                        <td className="nowrap">{formatCoordinates(request)}</td>
                        <td>{request.packagesRequested}</td>
                        <td>{request.description}</td>
                        <td className="muted">{request.createdByName ?? '—'}</td>
                        <td className="right">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={approve.isPending}
                            onClick={() => confirmApprove(request)}
                          >
                            Genehmigen
                          </button>{' '}
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            disabled={remove.isPending}
                            onClick={() => confirmDelete(request, 'ablehnen und entfernen')}
                          >
                            Ablehnen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <h2>Offene SD-Anfragen</h2>
          {!requests.isPending && approved.length === 0 && (
            <p className="muted center pad">Aktuell keine genehmigten SD-Anfragen.</p>
          )}
          {approved.map((request) => (
            <SdRequestRow
              key={request.id}
              request={request}
              worldKey={activeWorld}
              canDelete={isLeader || request.createdBy === user.id}
              onDelete={() => confirmDelete(request, 'entfernen')}
            />
          ))}

          {!isLeader && ownPending.length > 0 && (
            <>
              <h2>Meine Anfragen (warten auf Genehmigung)</h2>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Dorf</th>
                      <th>Pakete</th>
                      <th>Beschreibung</th>
                      <th className="right">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ownPending.map((request) => (
                      <tr key={request.id}>
                        <td className="nowrap">{formatCoordinates(request)}</td>
                        <td>{request.packagesRequested}</td>
                        <td>{request.description}</td>
                        <td className="right">
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            disabled={remove.isPending}
                            onClick={() => confirmDelete(request, 'zurückziehen')}
                          >
                            Zurückziehen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

function NewSdRequestForm({ worldKey }: { worldKey: string }) {
  const [coordinates, setCoordinates] = useState('');
  const [packagesRequested, setPackagesRequested] = useState('');
  const [description, setDescription] = useState('');
  const create = useCreateSdRequest(worldKey);
  const toast = useToast();

  const parsedCoordinates = parseCoordinates(coordinates);
  const valid = parsedCoordinates !== null && Number(packagesRequested) > 0 && description.trim() !== '';

  const submit = () => {
    if (!parsedCoordinates || !valid) return;
    create.mutate(
      {
        villageX: parsedCoordinates.x,
        villageY: parsedCoordinates.y,
        packagesRequested: Number(packagesRequested),
        description: description.trim(),
      },
      {
        onSuccess: () => {
          toast.push('success', 'SD-Anfrage wurde gestellt und wartet auf Genehmigung.');
          setCoordinates('');
          setPackagesRequested('');
          setDescription('');
        },
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  return (
    <div className="form" style={{ marginBottom: '16px' }}>
      <div className="field-row">
        <label className="field">
          <span>Dorf</span>
          <input
            type="text"
            value={coordinates}
            onChange={(event) => setCoordinates(event.target.value)}
            placeholder="574|874"
          />
        </label>
        <label className="field">
          <span>Pakete</span>
          <input
            type="number"
            min={1}
            value={packagesRequested}
            onChange={(event) => setPackagesRequested(event.target.value)}
            placeholder="20"
          />
        </label>
        <label className="field grow">
          <span>Beschreibung</span>
          <input
            type="text"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="z. B. wird geadelt, dringend"
          />
        </label>
        <button type="button" className="btn btn-primary" disabled={!valid || create.isPending} onClick={submit}>
          {create.isPending ? 'Wird gestellt …' : 'SD-Anfrage stellen'}
        </button>
      </div>
      <small className="hint">Muss von einem Stammesführer genehmigt werden, bevor sie für alle sichtbar ist.</small>
    </div>
  );
}

function SdRequestRow({
  request,
  worldKey,
  canDelete,
  onDelete,
}: {
  request: SdRequest;
  worldKey: string;
  canDelete: boolean;
  onDelete: () => void;
}) {
  const [amount, setAmount] = useState('');
  const contribute = useContributeToSdRequest(worldKey);
  const toast = useToast();

  const submit = () => {
    const parsed = Number(amount);
    if (!Number.isInteger(parsed) || parsed <= 0) return;
    contribute.mutate(
      { id: request.id, amount: parsed },
      {
        onSuccess: (result) => {
          setAmount('');
          toast.push(
            'success',
            result.fulfilled
              ? `SD-Anfrage für ${formatCoordinates(request)} ist erfüllt und wurde entfernt.`
              : `${parsed} Pakete für ${formatCoordinates(request)} eingetragen.`,
          );
        },
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  return (
    <div className="card" style={{ marginBottom: '12px' }}>
      <div className="card-head">
        <div>
          <h3 style={{ margin: 0 }}>
            {formatCoordinates(request)}{' '}
            <span className="badge badge-pending">
              {request.packagesRemaining} von {request.packagesRequested} offen
            </span>
          </h3>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {request.description}
          </p>
          <p className="muted" style={{ margin: '2px 0 0', fontSize: '0.85em' }}>
            Angefragt von {request.createdByName ?? '—'}
          </p>
        </div>
        {canDelete && (
          <button type="button" className="btn btn-danger btn-sm" onClick={onDelete}>
            Löschen
          </button>
        )}
      </div>

      {request.contributions.length > 0 && (
        <ul className="muted" style={{ margin: '8px 0', paddingLeft: '18px' }}>
          {request.contributions.map((contribution) => (
            <li key={contribution.id}>
              {contribution.userName ?? 'Unbekannt'}: {contribution.amount} Pakete (
              {new Date(contribution.createdAt).toLocaleDateString('de-DE')})
            </li>
          ))}
        </ul>
      )}

      <div className="field-row" style={{ marginTop: '8px' }}>
        <label className="field">
          <span>Ich schicke</span>
          <input
            type="number"
            min={1}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="Anzahl Pakete"
          />
        </label>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!Number.isInteger(Number(amount)) || Number(amount) <= 0 || contribute.isPending}
          onClick={submit}
        >
          Beitragen
        </button>
      </div>
    </div>
  );
}
